import { ErrorTranslation } from '@surface-elements/shared/util-error';

/**
 * Order-specific error translations based on actual API responses.
 * These override base translations when dealing with order-related endpoints.
 */
export const ORDER_ERROR_TRANSLATIONS: ErrorTranslation[] = [
  // Specific API error: "orderId not found!"
  {
    code: 'orders.not_found',
    message:
      'The order could not be found. It may have been deleted or you may not have permission to view it.',
    title: 'Order Not Found',
    matcher: error =>
      error.status === 404 && error.error?.message === 'orderId not found!',
    priority: 10,
  },

  // Generic 404 on orders endpoint (fallback if specific message doesn't match)
  {
    code: 'orders.resource_not_found',
    message: 'The requested order resource could not be found.',
    title: 'Not Found',
    matcher: error => error.status === 404 && error.url?.includes('/orders'),
    priority: 5,
  },

  // Validation errors (422) from Mongoose
  {
    code: 'orders.validation_error',
    message:
      'The order information provided is invalid. Please check the highlighted fields and try again.',
    title: 'Validation Error',
    matcher: error => error.status === 422 && error.url?.includes('/orders'),
    priority: 6,
  },

  // 400 errors on orders endpoint
  {
    code: 'orders.bad_request',
    message:
      'The order request could not be processed. Please check your input and try again.',
    title: 'Invalid Order Request',
    matcher: error => error.status === 400 && error.url?.includes('/orders'),
    priority: 5,
  },

  // Post-invoice specific endpoint errors
  {
    code: 'orders.invoice_update_failed',
    message:
      'Unable to update invoice date for the selected orders. Please try again.',
    title: 'Invoice Update Failed',
    matcher: error => error.url?.includes('/orders/post-invoice'),
    priority: 7,
  },

  // 500 errors on orders endpoint
  {
    code: 'orders.server_error',
    message:
      'Unable to process your order request. Our team has been notified. Please try again later.',
    title: 'Order Processing Error',
    matcher: error => error.status === 500 && error.url?.includes('/orders'),
    priority: 5,
  },

  // Order creation specific errors
  {
    code: 'orders.creation_failed',
    message:
      'Unable to create the order. Please verify all required information is provided and try again.',
    title: 'Order Creation Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/orders') &&
      error.url?.endsWith('/orders'), // POST to /orders
    priority: 6,
  },

  // Order update specific errors
  {
    code: 'orders.update_failed',
    message:
      'Unable to update the order. The order may have been modified by another user or locked for processing.',
    title: 'Order Update Failed',
    matcher: error =>
      error.status >= 400 &&
      error.url?.includes('/orders/') && // PATCH to /orders/:id
      !error.url?.includes('post-invoice'),
    priority: 6,
  },
];

/**
 * Provider configuration for order error translations
 */
export const ORDER_ERROR_PROVIDERS = [
  {
    provide: 'ERROR_TRANSLATION_CONFIG',
    useValue: {
      translations: ORDER_ERROR_TRANSLATIONS,
      appName: 'orders-domain',
    },
    multi: true,
  },
];
